import * as React from 'react'
import { invariant } from '@epic-web/invariant'
import { Cross2Icon } from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { isHotkey } from 'is-hotkey'
import {
  Block,
  getValueBasedOnSelectedVariants,
  getBlockUpdateOverride,
  isInstanceBlock,
  type SelectBlock,
} from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import Selector, {
  SelectorEmptyState,
  SelectorItem,
} from '~/sotano-kit/primitives/selector'
import { resolveSelectBlockAcceptedValuesFromValue } from '@basehub/data/replicache/schema/block/select-utils'
import { IconButton } from '~/sotano-kit/primitives/icon-button'

import { type ComboboxOption } from '~/hooks/use-combobox-state'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'

export const SelectBlockInput = ({
  block,
  path,
  parent,
  canCreateNewOptions,
  readOnly,
}: {
  block: SelectBlock
  path: string
  parent: Block
  canCreateNewOptions?: boolean
  readOnly?: boolean
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const editorMode = useEditorMode()
  const isReadOnly = readOnly || editorMode === 'read'
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const undoManager = useRepoUndoManager()
  const focusValueOp = React.useMemo(() => {
    return getBlockFocusOp({
      block: { id: block.id, sourceBlockId: block.sourceBlockId },
      preferredTabKey: activeTabKey,
      focusType: 'block:value',
    })
  }, [block.id, block.sourceBlockId, activeTabKey])

  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const blockValue = React.useMemo(
    () =>
      getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
      }),
    [block, selectedDashboardVariants]
  )

  const onSelect = React.useCallback(
    async (selectedOption: ComboboxOption) => {
      const newValue = selectedOption.value.trimEnd()
      const { newAcceptedValues, selectedValueIsNew } =
        resolveSelectBlockAcceptedValuesFromValue(
          { constraints: block.constraints },
          newValue
        )

      invariant(
        selectedValueIsNew ? canCreateNewOptions : true,
        "canCreateNewOptions prop should be true if there's a new value"
      )

      const params: UpdateBlockParams = {
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: getBlockUpdateOverride({
          selectedVariants: selectedDashboardVariants,
          block,
          newValue: block.constraints.multiple
            ? blockValue.includes(newValue)
              ? blockValue.filter((item) => item !== newValue)
              : [...blockValue, newValue]
            : [newValue],
          extend: {
            ...(selectedValueIsNew
              ? { constraints: { acceptedValues: newAcceptedValues } }
              : {}),
          },
        }),
        strategy: 'merge-deep',
        path,
        isoNow: new Date().toISOString(),
      }
      const result = await replicache?.mutate.updateBlock(params)
      if (result?.undo) {
        undoManager.current?.push({
          redo: [focusValueOp, { op: 'updateBlock', args: params }],
          undo: [
            focusValueOp,
            { op: 'blockBatchMutations', args: { mutations: result.undo } },
          ],
        })
      }
      return result
    },
    [
      blockValue,
      block,
      canCreateNewOptions,
      focusValueOp,
      path,
      replicache?.mutate,
      selectedDashboardVariants,
      undoManager,
    ]
  )

  const renderResult = React.useCallback(
    (search: string) => {
      if (!blockValue.length) {
        if (search) return

        return (
          <p className="text-dark-muted absolute">
            Select your value{block.constraints.multiple && 's'}
          </p>
        )
      }

      if (blockValue.length === 1 && !block.constraints.multiple) {
        if (search) return

        return <p className="leading-none absolute">{blockValue[0]}</p>
      }

      return blockValue.map((value, index) => (
        <p key={index} className="rounded-md flex items-center relative pr-7">
          <span className="leading-none">{value}</span>
          {block.constraints.multiple && !isReadOnly ? (
            <IconButton
              disabled={!block.constraints.multiple}
              className="absolute right-0.5 cursor-default pointer-events-auto disabled:invisible disabled:pointer-events-none p-0.5 text-dark-muted enabled:hover:text-dark-dim enabled:hover:!bg-dark-elevated"
              onClick={(e) => {
                e.stopPropagation()
                onSelect({ label: value, value })
              }}
            >
              <Cross2Icon width={14} height={14} />
            </IconButton>
          ) : (
            <div className="absolute right-1 text-dark-faint h-[14px] w-[14px] text-lg flex items-center justify-center">
              -
            </div>
          )}
        </p>
      ))
    },
    [block.constraints.multiple, blockValue, onSelect, isReadOnly]
  )

  const options = React.useMemo(() => {
    return block.constraints.acceptedValues
      .map(
        (value) => ({
          label: value,
          value: value,
          keywords: [value],
        }),
        []
      )
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [block.constraints.acceptedValues])

  const [search, setSearch] = React.useState('')

  return (
    <>
      <Selector
        id={`${idToSourceBlockId(block)}-select-input`}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        search={search}
        setSearch={setSearch}
        options={(search) => {
          // The user search is exactly another value, so we don't add a create <search-value> options
          const exactSearchExists = options.find(
            ({ value }) => value === search
          )

          if (exactSearchExists || !search) {
            if (!block.constraints.multiple) return options
            return options.filter(({ value }) => !blockValue.includes(value))
          }

          let filtered = block.constraints.multiple
            ? options.filter(({ value }) => !blockValue.includes(value))
            : options
          if (canCreateNewOptions) {
            filtered = filtered.concat([
              {
                label: `Create "${search}"`,
                value: search,
                keywords: [search],
              },
            ])
          }

          return filtered
        }}
        className="!rounded-md"
        renderResult={renderResult}
        onSelect={onSelect}
        closeAfterSelect={!block.constraints.multiple}
        onRemove={
          blockValue.length > 0
            ? async () => {
                const params: UpdateBlockParams = {
                  id: block.id,
                  instanceChildTarget: block.instanceChildTarget,
                  data: getBlockUpdateOverride({
                    block,
                    newValue: [],
                    selectedVariants: selectedDashboardVariants,
                  }),
                  strategy: 'merge',
                  path,
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache?.mutate.updateBlock(params)
                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [focusValueOp, { op: 'updateBlock', args: params }],
                    undo: [
                      focusValueOp,
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                    ],
                  })
                }
              }
            : undefined
        }
        onKeyDown={async (e) => {
          if (blockValue.length <= 1 && !block.constraints.multiple) return
          if (!e.target) return
          const input = e.target as HTMLInputElement
          if (input.value) return
          if (e.key !== 'Backspace' && !isHotkey('meta+backspace', e)) return

          const newValue = isHotkey('meta+backspace', e) ? [] : [...blockValue]
          newValue.pop()

          const params: UpdateBlockParams = {
            id: block.id,
            instanceChildTarget: block.instanceChildTarget,
            data: getBlockUpdateOverride({
              block,
              newValue,
              selectedVariants: selectedDashboardVariants,
            }),
            strategy: 'merge',
            path,
            isoNow: new Date().toISOString(),
          }
          const result = await replicache?.mutate.updateBlock(params)
          if (result?.undo) {
            undoManager.current?.push({
              redo: [focusValueOp, { op: 'updateBlock', args: params }],
              undo: [
                focusValueOp,
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
              ],
            })
          }
        }}
      >
        {({ options, getOptionProps, selectedIndex, search }) => {
          if (!options.length) {
            return (
              <SelectorEmptyState>
                {search ? (
                  <>No options found with that query.</>
                ) : (
                  <>
                    No options to choose from.
                    {!isInstanceBlock(parent) &&
                      ' Start typing to create a value.'}
                  </>
                )}
              </SelectorEmptyState>
            )
          }

          return options.map((option, index) => {
            const isHighlighted = selectedIndex === index
            const isSelected = blockValue.includes(option.value)

            return (
              <SelectorItem
                {...getOptionProps(index)}
                key={option.value}
                className={clsx(
                  isSelected && 'text-dark-control-highlight font-medium',
                  isHighlighted && 'bg-dark-shade-hover outline-none',
                  'relative flex h-8 cursor-default justify-between whitespace-nowrap select-none items-center rounded px-3 py-1.5 text-xs leading-none outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                )}
              >
                <span className="text-ellipsis overflow-hidden leading-normal w-full pr-2">
                  {!block.constraints.acceptedValues.includes(option.value)
                    ? `Create "${search}"`
                    : option.label}
                </span>
              </SelectorItem>
            )
          })
        }}
      </Selector>
    </>
  )
}
